/**
 * <DemoDataBanner /> — Surfaces clearly on patent sub-system pages
 * so that authenticated customers KNOW the numbers they're seeing are
 * synthetic demo data ("reduction-to-practice" seed), and provides a
 * one-click route to /cloud-integrations to wire their real stack.
 *
 * Hidden for:
 *   - Public/anonymous visitors (it's marketing for them)
 *   - The demo user (demo@cartieai.com — they intentionally see seeded data)
 *   - Any tenant that already has real connector(s) live
 *
 * Why this matters: prevents the "wait, I just paid for Pro and I'm
 * looking at someone named rahul.m's commit" trust break.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FlaskConical, ArrowRight, X } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DemoDataBanner = ({ learnMoreHref }) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) return; // anonymous → no banner
    const userEmail = (() => {
      try { return JSON.parse(localStorage.getItem('user') || '{}')?.email || ''; } catch { return ''; }
    })();
    if (userEmail === 'demo@cartieai.com') return; // demo user intentionally sees seed

    (async () => {
      try {
        const r = await axios.get(`${API_URL}/allocation/connector-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const sources = r.data?.sources || {};
        const anyLive = Object.values(sources).some((s) => s && s.live === true);
        if (!cancelled) setShouldShow(!anyLive);
      } catch {
        // if API fails, default to showing (better safe than confusingly wrong)
        if (!cancelled) setShouldShow(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!shouldShow || dismissed) return null;

  return (
    <div
      className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3 shadow-sm"
      data-testid="demo-data-banner"
    >
      <FlaskConical className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-amber-900 font-semibold">
          You're viewing demo data
        </div>
        <div className="text-xs text-amber-800 mt-1">
          These numbers are <span className="font-semibold text-amber-900">synthetic seed data</span> to showcase the
          engine. To see <span className="font-semibold text-emerald-700">your</span> data, connect a cloud account.{" "}
          {learnMoreHref && (
            <a href={learnMoreHref} target="_blank" rel="noreferrer" className="text-amber-900 underline hover:text-amber-700 font-medium">
              Learn more
            </a>
          )}
        </div>
      </div>
      <Link
        to="/cloud-integrations"
        className="text-xs font-semibold px-3 py-1.5 rounded-md text-white shrink-0 flex items-center gap-1.5 shadow-sm hover:shadow-md transition-shadow"
        style={{background:'linear-gradient(135deg, rgb(59,130,246), rgb(6,182,212), rgb(16,185,129))'}}
        data-testid="demo-banner-connect-btn"
      >
        Connect cloud <ArrowRight className="w-3 h-3" />
      </Link>
      <button
        onClick={() => setDismissed(true)}
        className="text-amber-700 hover:text-amber-900 shrink-0"
        data-testid="demo-banner-dismiss-btn"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default DemoDataBanner;
